/**
 * Thick slab MPR 2D: proyección CPU (MIP / MinIP / Avg) sobre el stack nativo.
 * Salida Int16Array para loadImageFromCustomObject (Cornerstone).
 */

export const SLAB_MODES = ['thin', 'mip', 'minip', 'avg']
export const SLAB_MM_PRESETS = [0, 3, 5, 10]

export function mmToHalfWidth(slabMm, spacingAlongNormal) {
  const mm = Number(slabMm)
  const sp = Number(spacingAlongNormal)
  if (!(mm > 0) || !(sp > 0)) return 0
  return Math.max(0, Math.round(mm / sp / 2))
}

export function clampHalfWidth(k, maxK) {
  const n = Number(k)
  const cap = Number(maxK)
  if (!Number.isFinite(n) || n <= 0) return 0
  if (!Number.isFinite(cap) || cap <= 0) return 0
  return Math.min(Math.round(n), Math.round(cap))
}

export function slabHalfWidthFromMeta(mprData, spacingAlongNormal) {
  if (!mprData) return 0
  if (mprData.slabForceThin) return 0
  const mode = String(mprData.slabMode || 'thin').toLowerCase()
  if (mode === 'thin') return 0
  const k = mmToHalfWidth(mprData.slabMm, spacingAlongNormal)
  return clampHalfWidth(k, mprData.slabMaxHalfWidth)
}

export function slabModeLabel(mode) {
  const m = String(mode || 'thin').toLowerCase()
  if (m === 'mip') return 'MIP'
  if (m === 'minip') return 'MinIP'
  if (m === 'avg') return 'Avg'
  return 'Fino'
}

export function resolveSlice(volume, z, zSize) {
  if (!volume || !volume.length) return null
  const max = Math.max(1, zSize || volume.length)
  let zi = Math.max(0, Math.min(max - 1, z | 0))
  if (volume[zi]) return volume[zi]
  let n = zi
  while (n > 0 && !volume[n]) n--
  if (volume[n]) return volume[n]
  n = zi
  while (n < max - 1 && !volume[n]) n++
  return volume[n] || null
}

function finishAvg(dest, acc, cnt) {
  for (let i = 0; i < dest.length; i++) {
    dest[i] = cnt[i] ? (acc[i] / cnt[i]) | 0 : 0
  }
}

function ingest(mode, dest, seen, acc, cnt, i, val) {
  if (val == null || !Number.isFinite(val)) return
  const v = val | 0
  if (mode === 'mip') {
    if (!seen[i]) {
      dest[i] = v
      seen[i] = 1
    } else if (v > dest[i]) dest[i] = v
  } else if (mode === 'minip') {
    if (!seen[i]) {
      dest[i] = v
      seen[i] = 1
    } else if (v < dest[i]) dest[i] = v
  } else {
    acc[i] += v
    cnt[i] += 1
  }
}

/**
 * Coronal (XZ): X horizontal, Z vertical; slab a lo largo de Y.
 */
export function projectXzPlane({
  getSlice,
  xSize,
  ySize,
  zSize,
  yi,
  halfWidth,
  mode,
  flipZ = false,
  dest: destIn = null,
}) {
  const xs = Math.max(1, xSize | 0)
  const ys = Math.max(1, ySize | 0)
  const zs = Math.max(1, zSize | 0)
  const y0 = Math.max(0, Math.min(ys - 1, Math.round(yi)))
  const kMax = Math.max(0, halfWidth | 0)
  const need = xs * zs
  const dest =
    destIn && destIn.length === need ? destIn : new Int16Array(need)
  const useThin = mode === 'thin' || kMax === 0
  const proj = useThin ? 'thin' : String(mode || 'avg').toLowerCase()
  const seen = proj === 'avg' || useThin ? null : new Uint8Array(dest.length)
  const acc = proj === 'avg' ? new Int32Array(dest.length) : null
  const cnt = proj === 'avg' ? new Uint16Array(dest.length) : null

  for (let z = 0; z < zs; z++) {
    const slice = getSlice(z)
    if (!slice) continue
    const zOut = flipZ ? zs - 1 - z : z
    const rowBase = xs * zOut
    if (useThin) {
      const src = y0 * xs
      for (let x = 0; x < xs; x++) dest[x + rowBase] = slice[x + src]
      continue
    }
    for (let k = -kMax; k <= kMax; k++) {
      const yk = Math.max(0, Math.min(ys - 1, y0 + k))
      const src = yk * xs
      for (let x = 0; x < xs; x++) {
        ingest(proj, dest, seen, acc, cnt, x + rowBase, slice[x + src])
      }
    }
  }
  if (proj === 'avg') finishAvg(dest, acc, cnt)
  return dest
}

/**
 * Sagital (YZ): Y horizontal, Z vertical; slab a lo largo de X.
 */
export function projectYzPlane({
  getSlice,
  xSize,
  ySize,
  zSize,
  xi,
  halfWidth,
  mode,
  flipZ = false,
  dest: destIn = null,
}) {
  const xs = Math.max(1, xSize | 0)
  const ys = Math.max(1, ySize | 0)
  const zs = Math.max(1, zSize | 0)
  const x0 = Math.max(0, Math.min(xs - 1, Math.round(xi)))
  const kMax = Math.max(0, halfWidth | 0)
  const need = ys * zs
  const dest =
    destIn && destIn.length === need ? destIn : new Int16Array(need)
  const useThin = mode === 'thin' || kMax === 0
  const proj = useThin ? 'thin' : String(mode || 'avg').toLowerCase()
  const seen = proj === 'avg' || useThin ? null : new Uint8Array(dest.length)
  const acc = proj === 'avg' ? new Int32Array(dest.length) : null
  const cnt = proj === 'avg' ? new Uint16Array(dest.length) : null

  for (let z = 0; z < zs; z++) {
    const slice = getSlice(z)
    if (!slice) continue
    const zOut = flipZ ? zs - 1 - z : z
    const rowBase = ys * zOut
    if (useThin) {
      for (let y = 0; y < ys; y++) dest[y + rowBase] = slice[x0 + xs * y]
      continue
    }
    for (let k = -kMax; k <= kMax; k++) {
      const xk = Math.max(0, Math.min(xs - 1, x0 + k))
      for (let y = 0; y < ys; y++) {
        ingest(proj, dest, seen, acc, cnt, y + rowBase, slice[xk + xs * y])
      }
    }
  }
  if (proj === 'avg') finishAvg(dest, acc, cnt)
  return dest
}

/**
 * Axial: X horizontal, Y vertical; slab a lo largo de Z (cortes nativos).
 */
export function projectAxialPlane({
  getSlice,
  xSize,
  ySize,
  zSize,
  zi,
  halfWidth,
  mode,
}) {
  const xs = Math.max(1, xSize | 0)
  const ys = Math.max(1, ySize | 0)
  const zs = Math.max(1, zSize | 0)
  const z0 = Math.max(0, Math.min(zs - 1, Math.round(zi)))
  const kMax = Math.max(0, halfWidth | 0)
  const dest = new Int16Array(xs * ys)
  const useThin = mode === 'thin' || kMax === 0
  const proj = useThin ? 'thin' : String(mode || 'avg').toLowerCase()
  const seen = proj === 'avg' || useThin ? null : new Uint8Array(dest.length)
  const acc = proj === 'avg' ? new Int32Array(dest.length) : null
  const cnt = proj === 'avg' ? new Uint16Array(dest.length) : null
  const planeLen = xs * ys

  if (useThin) {
    const slice = getSlice(z0)
    if (slice) dest.set(slice.subarray ? slice.subarray(0, planeLen) : slice)
    return dest
  }

  for (let k = -kMax; k <= kMax; k++) {
    const zk = Math.max(0, Math.min(zs - 1, z0 + k))
    const slice = getSlice(zk)
    if (!slice) continue
    for (let i = 0; i < planeLen; i++) {
      ingest(proj, dest, seen, acc, cnt, i, slice[i])
    }
  }
  if (proj === 'avg') finishAvg(dest, acc, cnt)
  return dest
}
